/**
 * Générateur — Enchainer additions et soustractions de décimaux relatifs.
 * Attendu ID : 921 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 921.json) : « Enchainer additions et
 * soustractions de décimaux relatifs. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-22) :
 *
 *   Carte « reorganiser » — Réorganiser puis calculer (niveaux 1, 2 et 6)
 *     texte_trou_saisie : la démarche en QUATRE TEMPS de la spécification —
 *     réécriture avec tous les termes signés, somme des positifs, somme des
 *     négatifs, résultat. L'énoncé peut être entièrement parenthésé et la
 *     correction en écriture simplifiée (niveau 6) ;
 *     qcm_unique : distracteurs = les deux sommes de groupes ajoutées, un
 *     terme compté deux fois, une seule parenthèse transformée.
 *
 *   Carte « etapes » — Enchaînements en écriture simplifiée (niveau 3)
 *     texte_trou_saisie : cinq termes entiers en écriture simplifiée, le
 *     résultat seul ; tableau_vf : cinq gabarits à prédicat variable.
 *
 *   Carte « astuce » — Regrouper astucieusement (niveaux 4 et 5)
 *     qcm_unique (la paire de termes opposés, unique par construction) et
 *     texte_trou_saisie (le résultat après regroupement).
 *
 *   Carte « mixte » — Enchaînements mixtes (niveaux 4 et 5)
 *     texte_trou_saisie : le résultat seul, l'écriture de l'énoncé étant tirée
 *     dans la liste fermée. La consigne annonce que seul le résultat est vérifié.
 *
 * Spécification (921.json) :
 *   Bornes appliquées : operations_autorisees = addition et soustraction
 *     seulement (`origine: prescrit`) ; nombre_de_termes 3..6 et
 *     nombre_d_operations 2..5 (déduit) ;
 *     nombre_de_decimales_de_chaque_terme 0..2, une expression entière à deux
 *     décimales étant produite ; termes_de_l_expression -100..100, aucun terme
 *     nul ; alternance_des_operations : les trois premières opérations ne sont
 *     jamais toutes identiques ; signes_des_termes : les trois premiers termes
 *     ne sont jamais tous de même signe, et l'expression comporte au moins un
 *     terme de chaque signe ; forme_d_ecriture_de_l_enonce : les trois
 *     écritures de la liste fermée, la forme de l'énoncé et celle de la
 *     correction étant distinctes (niveau 6).
 *   Exclusions : aucun terme nul ; aucun enchaînement réduit à une suite
 *     d'additions ou de soustractions ; aucune multiplication ni division ;
 *     aucune imbrication de parenthèses ; aucun terme à plus de deux décimales.
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code,
 *   _meta.params.lignes[].code, erreurVisee) :
 *     « Dérouler l'expression de gauche à droite en perdant un signe »
 *         → enchainement_gauche_droite (reorganiser ×2, etapes_vf)
 *     « Traiter tous les signes comme des signes d'opération, ou tous comme des
 *       signes de nombre » → signes_confondus_operation_nombre (etapes_vf)
 *     « Regrouper les positifs et les négatifs puis additionner les deux sommes »
 *         → groupes_additionnes_au_lieu_de_soustraits (reorganiser ×2)
 *     « Changer le signe d'un seul terme en supprimant une parenthèse précédée
 *       d'un signe moins » → un_seul_signe_change (reorganiser_qcm, etapes_vf)
 *     « Aligner mal les rangs décimaux » → rangs_decimaux_mal_alignes
 *         (etapes_vf, mixte quand les décimales des termes diffèrent)
 *     « Croire que l'ordre des termes ne peut pas être changé »
 *         → ordre_fige_interdit_le_regroupement (etapes_vf, astuce)
 *     « Oublier un terme lors de la réorganisation, ou en compter un deux fois »
 *         → terme_oublie_ou_double (reorganiser_qcm, astuce ×2)
 *   Graduation → cartes :
 *     niveau 1 (trois termes entiers, tout parenthésé) et niveau 2 (quatre
 *       termes, seuls les négatifs parenthésés) → carte reorganiser ;
 *     niveau 3 (cinq termes entiers, écriture simplifiée) → carte etapes ;
 *     niveaux 4 et 5 (une puis deux décimales) → cartes astuce et mixte ;
 *     niveau 6 (énoncé parenthésé, correction simplifiée) → carte reorganiser,
 *       dont la deuxième ligne est précisément l'écriture simplifiée.
 *   Non tranché : amplitude_maximale_reglable (8..100) n'a pas de mécanisme de
 *     réglage ; « série » de questions n'a pas d'équivalent ; la consigne
 *     d'écrit (« détaille les calculs ») n'a pas de support ici, seule la
 *     formulation interactive est produite.
 *
 * Arithmétique en CENTIÈMES entiers. Signe « - » = tiret clavier partout.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 921;

const L_REORGANISER = 'Réorganiser puis calculer';
const L_ETAPES = 'Enchaînements en écriture simplifiée';
const L_ASTUCE = 'Regrouper astucieusement';
const L_MIXTE = 'Enchaînements mixtes';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Centièmes entiers → écriture française. cent(0) = « 0 ». */
const cent = (c) => {
    const s = c < 0 ? '-' : '';
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const r = a % 100;
    if (r === 0) return `${s}${ent}`;
    if (r % 10 === 0) return `${s}${ent},${r / 10}`;
    return `${s}${ent},${String(r).padStart(2, '0')}`;
};
const signe = (c) => (c === 0 ? '0' : c < 0 ? cent(c) : `+${cent(c)}`);
const paren = (c) => `(${signe(c)})`;

const CONSIGNE_RES = 'Écris chaque réponse en écriture décimale ; un résultat positif peut s\'écrire avec ou sans le signe +.';
const SEUL_RESULTAT = 'Seul le résultat final est vérifié.';

/** Un terme non nul, |v| ≤ 50, avec exactement `dec` décimales. */
const tirerTerme = (dec) => {
    if (dec === 0) return randInt(1, 50) * 100;
    if (dec === 1) {
        let k = randInt(1, 499);
        if (k % 10 === 0) k += 1;
        return k * 10;
    }
    let k = randInt(1, 4999);
    if (k % 10 === 0) k += 1;
    return k;
};

const sommeDe = (t) => t.reduce((a, b) => a + b, 0);

/**
 * Une expression : premier terme t0 et jonctions { op, v }. La CONTRIBUTION
 * d'une jonction vaut op === '+' ? v : -v. Contraintes de la spec :
 *   - aucun terme nul, |v| ≤ 100, 0 à 2 décimales ;
 *   - au moins un terme de chaque signe, et les trois premiers termes jamais
 *     tous de même signe ;
 *   - les opérateurs ne sont jamais tous identiques, et les trois premiers non
 *     plus (alternance_des_operations).
 */
const tirerExpression = (nTermes, decs) => {
    const valide = (e) => {
        const c = [e.t0, ...e.jonctions.map(j => (j.op === '+' ? j.v : -j.v))];
        const ops = e.jonctions.map(j => j.op);
        const troisPremiers = c.slice(0, 3);
        // Les opérateurs de l'écriture simplifiée sont les SIGNES des
        // contributions : l'alternance et l'exclusion « ni suite d'additions ni
        // suite de soustractions » doivent tenir dans les trois écritures.
        const signesOps = c.slice(1).map(v => v > 0);
        return c.some(v => v > 0) && c.some(v => v < 0)
            && !troisPremiers.every(v => v > 0) && !troisPremiers.every(v => v < 0)
            && new Set(ops).size > 1
            && new Set(ops.slice(0, 3)).size > 1
            && new Set(signesOps).size > 1
            && new Set(signesOps.slice(0, 3)).size > 1;
    };
    const construire = () => ({
        t0: randomChoice([1, -1]) * tirerTerme(randomChoice(decs)),
        jonctions: Array.from({ length: nTermes - 1 }, () => ({
            op: randomChoice(['+', '-']),
            v: randomChoice([1, -1]) * tirerTerme(randomChoice(decs)),
            nu: randomChoice([true, false]),
        })),
    });
    let e = construire();
    for (let essai = 0; essai < 80 && !valide(e); essai++) e = construire();
    if (!valide(e)) { // repli déterministe, vérifié à la main
        // Repli déterministe, vérifié à la main : contributions +5 / -3 / -8 /
        // +2 (les trois premiers termes ne sont pas de même signe, les
        // opérateurs alternent dans les trois écritures).
        e = {
            t0: 500,
            jonctions: [
                { op: '-', v: 300, nu: false },
                { op: '+', v: -800, nu: false },
                { op: '-', v: -200, nu: false },
                { op: '+', v: 400, nu: false },
            ].slice(0, nTermes - 1),
        };
    }
    return e;
};

const contributions = (e) => [e.t0, ...e.jonctions.map(j => (j.op === '+' ? j.v : -j.v))];

// ═════════ Écritures de l'énoncé (liste fermée de la spec) ═════════

const FORMES = ['tous', 'negatifs', 'simplifiee'];

const ecrire = (e, forme) => {
    if (forme === 'simplifiee') {
        const c = contributions(e);
        return [cent(c[0]), ...c.slice(1).map(v => `${v < 0 ? '-' : '+'} ${cent(Math.abs(v))}`)].join(' ');
    }
    if (forme === 'tous') {
        return [paren(e.t0), ...e.jonctions.map(j => `${j.op} ${paren(j.v)}`)].join(' ');
    }
    const tete = e.t0 < 0 ? `(${cent(e.t0)})` : cent(e.t0);
    return [tete, ...e.jonctions.map(j => `${j.op} ${j.v < 0 ? `(${cent(j.v)})` : cent(j.v)}`)].join(' ');
};

/** L'écriture simplifiée avec les signes en trous (la réécriture signée). */
const reecritureTrous = (e) => {
    const c = contributions(e);
    return [cent(c[0]), ...c.slice(1).map(v => `{{${v < 0 ? '-' : '+'}}} ${cent(Math.abs(v))}`)].join(' ');
};

/** Décalage des rangs décimaux : 2,5 devient 2,05 (erreur visée 5). */
const malAligne = (c) => {
    const s = c < 0 ? -1 : 1;
    const a = Math.abs(c);
    return s * (Math.floor(a / 100) * 100 + Math.floor((a % 100) / 10));
};

/** Tire nb vérités avec au moins une vraie et une fausse. */
const tirerVerites = (nb) => {
    const v = [];
    for (let i = 0; i < nb; i++) v.push(randomChoice([true, false]));
    if (v.every(x => x)) v[randInt(0, nb - 1)] = false;
    else if (v.every(x => !x)) v[randInt(0, nb - 1)] = true;
    return v;
};

const LETTRES_EXPR = ['A', 'B', 'C', 'D', 'E', 'S'];

// ═════════ Carte « reorganiser » — la démarche en quatre temps ═════════

const genReorganiser = () => {
    // Niveau 1 : trois termes entiers, tout parenthésé. Niveau 2 : quatre
    // termes, seuls les négatifs parenthésés. Niveau 6 : énoncé entièrement
    // parenthésé, correction en écriture simplifiée (la deuxième ligne).
    const cfg = randomChoice([
        { niveau: 1, n: 3, decs: [0], forme: 'tous' },
        { niveau: 2, n: 4, decs: [0], forme: 'negatifs' },
        { niveau: 6, n: randInt(4, 6), decs: [0, 1, 2], forme: 'tous' },
    ]);
    const e = tirerExpression(cfg.n, cfg.decs);
    const c = contributions(e);
    const P = sommeDe(c.filter(v => v > 0));
    const N = sommeDe(c.filter(v => v < 0));
    const lettre = randomChoice(LETTRES_EXPR);
    const phrase = `${lettre} = ${ecrire(e, cfg.forme)}\n`
        + `Écriture simplifiée : ${reecritureTrous(e)}\n`
        + `Somme des termes positifs : {{${cent(P)}}}\n`
        + `Somme des termes négatifs : {{${cent(N)}}}\n`
        + `Résultat : {{${cent(P + N)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Réécris ${lettre} sans parenthèses, regroupe les termes positifs et les termes négatifs, puis calcule.</b> `
        + `${CONSIGNE_RES} Chaque champ est vérifié séparément.</p>`,
        { ...opts(L_REORGANISER), mode: 'saisie' });
    payload._meta.params = {
        t0: e.t0, jonctions: e.jonctions, forme: cfg.forme, niveau: cfg.niveau, P, N,
        erreurVisee: 'enchainement_gauche_droite',
    };
    return payload;
};

const genReorganiserQcm = () => {
    const construire = () => {
        const cfg = randomChoice([
            { niveau: 2, n: 4, decs: [0] },
            { niveau: 5, n: randInt(4, 5), decs: [1, 2] },
        ]);
        const e = tirerExpression(cfg.n, cfg.decs);
        const c = contributions(e);
        const total = sommeDe(c);
        const P = sommeDe(c.filter(v => v > 0));
        const N = sommeDe(c.filter(v => v < 0));
        const double = randomChoice(c);
        // Une seule parenthèse transformée : la jonction « - (v) » garde le
        // signe de v au lieu de le changer.
        const k = e.jonctions.findIndex(j => j.op === '-');
        const propositions = [total, P - N, total + double, total - 2 * (k < 0 ? 0 : -e.jonctions[k].v)];
        return new Set(propositions).size === 4 && k >= 0 ? { e, cfg, total, propositions } : null;
    };
    let t = construire();
    for (let essai = 0; essai < 80 && !t; essai++) t = construire();
    if (!t) { // repli déterministe, vérifié à la main
        const e = {
            t0: 500,
            jonctions: [{ op: '-', v: 300, nu: false }, { op: '+', v: -800, nu: false }],
        };
        t = { e, cfg: { niveau: 2 }, total: -600, propositions: [-600, 1600, -1200, 0] };
    }
    const forme = randomChoice(FORMES);
    const lettre = randomChoice(LETTRES_EXPR);
    const codes = ['groupes_additionnes_au_lieu_de_soustraits', 'terme_oublie_ou_double', 'un_seul_signe_change'];
    const distracteurs = t.propositions.slice(1).map((v, i) => ({ valeur: cent(v), code: codes[i] }));
    const payload = makeQcm(t.propositions.map(cent), cent(t.total),
        `<p><b>Que vaut ${lettre} = ${ecrire(t.e, forme)} ?</b></p>`,
        opts(L_REORGANISER));
    payload._meta.params = {
        t0: t.e.t0, jonctions: t.e.jonctions, forme, niveau: t.cfg.niveau, distracteurs,
    };
    return payload;
};

// ═════════ Carte « etapes » — écriture simplifiée (niveau 3) ═════════

const genEtapes = () => {
    const e = tirerExpression(5, [0]);
    const lettre = randomChoice(LETTRES_EXPR);
    const payload = makeTexteTrou(`${lettre} = ${ecrire(e, 'simplifiee')}\n${lettre} = {{${cent(sommeDe(contributions(e)))}}}`,
        `<p><b>Effectue le calcul.</b> ${CONSIGNE_RES} ${SEUL_RESULTAT}</p>`,
        { ...opts(L_ETAPES), mode: 'saisie' });
    payload._meta.params = {
        t0: e.t0, jonctions: e.jonctions, forme: 'simplifiee', niveau: 3,
        erreurVisee: 'ordre_fige_interdit_le_regroupement',
    };
    return payload;
};

/** Gabarits à prédicat variable. ctx : { e, expr, total, x, y, z, p, n }. */
const GABARITS_ETAPES = [
    {
        id: 'resultat', code: 'enchainement_gauche_droite',
        ligne: (v, c) => `${c.expr} = ${cent(v ? c.total : -c.total)}.`,
    },
    {
        id: 'signes', code: 'signes_confondus_operation_nombre',
        ligne: (v, c) => `Dans l'écriture ${c.expr}, le nombre ${cent(c.premierNegatif)} `
            + `${v ? 'est' : 'n\'est pas'} un terme de la somme.`,
    },
    {
        id: 'ordre', code: 'ordre_fige_interdit_le_regroupement',
        ligne: v => (v
            ? 'Dans une somme de nombres relatifs, on peut changer l\'ordre des termes sans changer le résultat.'
            : 'Dans une somme de nombres relatifs, on ne peut pas changer l\'ordre des termes.'),
    },
    {
        id: 'un_signe', code: 'un_seul_signe_change',
        ligne: (v, c) => `${cent(c.p)} - ${paren(c.n)} - ${paren(-c.n)} s'écrit sans parenthèses `
            + `${cent(c.p)} + ${cent(-c.n)} ${v ? '-' : '+'} ${cent(-c.n)}.`,
    },
    {
        id: 'alignement', code: 'rangs_decimaux_mal_alignes',
        ligne: (v, c) => `${cent(c.x)} + ${cent(c.y)} - ${cent(c.z)} = `
            + `${cent(v ? c.x + c.y - c.z : malAligne(c.x) + c.y - c.z)}.`,
    },
];

const genEtapesVf = () => {
    let e = tirerExpression(randInt(3, 5), [0, 1]);
    for (let essai = 0; essai < 40 && sommeDe(contributions(e)) === 0; essai++) {
        e = tirerExpression(randInt(3, 5), [0, 1]);
    }
    const c = contributions(e);
    const ctx = {
        e,
        expr: ecrire(e, 'simplifiee'),
        total: sommeDe(c),
        premierNegatif: c.find(v => v < 0),
        x: tirerTerme(1),
        y: tirerTerme(2),
        z: tirerTerme(0),
        p: tirerTerme(0),
        n: -tirerTerme(randomChoice([0, 1])),
    };
    const gabarits = shuffle(GABARITS_ETAPES).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({
        template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_ETAPES));
    payload._meta.params = { t0: e.t0, jonctions: e.jonctions, forme: 'simplifiee', niveau: 3, lignes };
    return payload;
};

// ═════════ Carte « astuce » — regrouper astucieusement (niveaux 4 et 5) ═════════

/** Cinq contributions dont UNE SEULE paire opposée ; valeurs absolues distinctes. */
const tirerAstuce = () => {
    const dec = randomChoice([1, 2]);
    const x = tirerTerme(dec);
    const absolues = new Set([x]);
    const autres = [];
    for (let essai = 0; essai < 80 && autres.length < 3; essai++) {
        const v = tirerTerme(randomChoice([0, 1, 2]));
        if (!absolues.has(v)) { absolues.add(v); autres.push(randomChoice([1, -1]) * v); }
    }
    [400, -900, 1300, -2150].forEach((v) => { // repli déterministe
        if (autres.length < 3 && !absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); autres.push(v); }
    });
    // L'ordre doit respecter les mêmes contraintes que les autres cartes :
    // ni suite d'additions ni suite de soustractions, et les trois premiers
    // termes jamais tous de même signe.
    const ordreOk = (t) => {
        const ops = t.slice(1).map(v => v > 0);
        const trois = t.slice(0, 3);
        return new Set(ops).size > 1 && new Set(ops.slice(0, 3)).size > 1
            && !trois.every(v => v > 0) && !trois.every(v => v < 0);
    };
    let contribs = shuffle([x, -x, ...autres]);
    for (let essai = 0; essai < 60 && !ordreOk(contribs); essai++) contribs = shuffle(contribs);
    if (!ordreOk(contribs)) { // repli déterministe : le signe minoritaire en deuxième position
        const pos = contribs.filter(v => v > 0);
        const neg = contribs.filter(v => v < 0);
        const minor = pos.length <= neg.length ? pos : neg;
        const major = pos.length <= neg.length ? neg : pos;
        contribs = [major[0], minor[0], ...major.slice(1), ...minor.slice(1)];
    }
    return {
        x, contribs, niveau: dec === 1 ? 4 : 5,
        expr: [cent(contribs[0]), ...contribs.slice(1).map(v => `${v < 0 ? '-' : '+'} ${cent(Math.abs(v))}`)].join(' '),
        reste: sommeDe(autres),
    };
};

const genAstuceQcm = () => {
    const a = tirerAstuce();
    const [p1, p2] = a.contribs.filter(c => Math.abs(c) === Math.abs(a.x));
    const correct = `${cent(p1)} et ${cent(p2)}`;
    const faux = [];
    const indices = shuffle(a.contribs.flatMap((_, i) => a.contribs.map((__, j) => [i, j]).filter(([u, w]) => u < w)));
    // Les paires distractrices sont de SIGNES CONTRAIRES (une paire de même
    // signe ne peut jamais être opposée) et, tant que c'est possible, ne
    // contiennent aucun terme de la paire cherchée : sans cela, la bonne
    // réponse se lirait à la valeur qui revient dans les quatre propositions.
    const ajouter = (eviterLaPaire) => {
        for (const [i, j] of indices) {
            if (faux.length >= 3) return;
            const s1 = a.contribs[i];
            const s2 = a.contribs[j];
            if (s1 + s2 === 0 || (s1 > 0) === (s2 > 0)) continue;
            if (eviterLaPaire && (Math.abs(s1) === Math.abs(a.x) || Math.abs(s2) === Math.abs(a.x))) continue;
            const t = `${cent(s1)} et ${cent(s2)}`;
            if (!faux.includes(t)) faux.push(t);
        }
    };
    ajouter(true);
    ajouter(false);
    const payload = makeQcm([correct, ...faux], correct,
        `<p>On veut calculer <strong>${a.expr}</strong> astucieusement. `
        + '<b>Quels sont les deux termes opposés, dont la somme vaut 0, à regrouper d\'abord ?</b></p>',
        opts(L_ASTUCE));
    payload._meta.params = {
        x: a.x, contribs: a.contribs, niveau: a.niveau,
        distracteurs: faux.map(v => ({ valeur: v, code: 'terme_oublie_ou_double' })),
    };
    return payload;
};

const genAstuceSaisie = () => {
    const a = tirerAstuce();
    const lettre = randomChoice(LETTRES_EXPR);
    const payload = makeTexteTrou(`${lettre} = ${a.expr}\nEn regroupant astucieusement, ${lettre} = {{${cent(a.reste)}}}`,
        `<p><b>Repère les deux termes opposés, regroupe, puis calcule.</b> ${CONSIGNE_RES} ${SEUL_RESULTAT}</p>`,
        { ...opts(L_ASTUCE), mode: 'saisie' });
    payload._meta.params = {
        x: a.x, contribs: a.contribs, reste: a.reste, niveau: a.niveau,
        erreurVisee: 'terme_oublie_ou_double',
    };
    return payload;
};

// ═════════ Carte « mixte » — le résultat seul (niveaux 4 et 5) ═════════

const genMixte = () => {
    const niveau = randomChoice([4, 5]);
    const e = tirerExpression(randInt(3, 6), niveau === 4 ? [1, 0] : [2, 1]);
    const forme = randomChoice(FORMES);
    const lettre = randomChoice(LETTRES_EXPR);
    const c = contributions(e);
    const payload = makeTexteTrou(`${lettre} = ${ecrire(e, forme)}\n${lettre} = {{${cent(sommeDe(c))}}}`,
        `<p><b>Effectue le calcul.</b> ${CONSIGNE_RES} ${SEUL_RESULTAT}</p>`,
        { ...opts(L_MIXTE), mode: 'saisie' });
    const decimalesMelees = new Set(c.map(v => (v % 100 === 0 ? 0 : (v % 10 === 0 ? 1 : 2)))).size > 1;
    payload._meta.params = {
        t0: e.t0, jonctions: e.jonctions, forme, niveau,
        erreurVisee: decimalesMelees ? 'rangs_decimaux_mal_alignes' : 'enchainement_gauche_droite',
    };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    reorganiser_texte_trou_saisie: genReorganiser,
    reorganiser_qcm_unique: genReorganiserQcm,
    etapes_texte_trou_saisie: genEtapes,
    etapes_vf: genEtapesVf,
    astuce_opposes_qcm_unique: genAstuceQcm,
    astuce_texte_trou_saisie: genAstuceSaisie,
    mixte_texte_trou_saisie: genMixte,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', vf: 'Vrai/Faux',
    opposes_qcm_unique: 'QCM Opposés',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'reorganiser', label: L_REORGANISER, formatsList: buildFormats('reorganiser') },
    { id: 'etapes', label: L_ETAPES, formatsList: buildFormats('etapes') },
    { id: 'astuce', label: L_ASTUCE, formatsList: buildFormats('astuce') },
    { id: 'mixte', label: L_MIXTE, formatsList: buildFormats('mixte') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
